import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Image } from "react-bootstrap";
import TypeWriter from "src/components/seek/typewriter";
import { FileService } from "src/services/file.service";
import styles from "./chatAnswer.module.scss";
import AssetIcon from "src/components/asset/icon";
import _ from "lodash";
import Slider from "src/components/slider";
import { useDispatch } from "react-redux";
import {
  fetchSeekEmailResponse,
  invalidateSeekEmailResponse,
  updateSeekFeedback,
} from "src/redux/actions/seek";
import { useSelector } from "react-redux";
import { deselectAsset, selectAsset } from "src/legacy/redux/actions/selection";
import cx from "classnames";
import AddToCollection from "src/pages/asset/components/addCollection";
import Tooltip from "src/components/tooltip";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { ASSET_DETAIL, SEEK } from "src/constants/adoption/sourceTypes";
import ComposeDialog from "src/components/compose/components/dialog";
import Text from "src/components/text";

interface ChatAnswerProps {
  index: number;
  chat: any;
  profile: any;
  handleComplete: (index: number) => void;
  skipTypewriting: boolean;
  showDivider?: boolean;
  seekAsset?: boolean;
  scrollToBottom?: () => void;
  setShowCompose: (show: boolean) => void;
  setAssetsForCompose: any;
}

const ChatAnswer: React.FC<ChatAnswerProps> = ({
  index,
  chat,
  profile,
  handleComplete,
  skipTypewriting,
  showDivider = true,
  seekAsset,
  setShowCompose,
  scrollToBottom,
  setAssetsForCompose
}) => {
  const dispatch = useDispatch();

  const [showAssets, setShowAssets] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [isHelpful, setIsHelpful] = useState(null);

  const asset = useSelector((state: any) => state.asset.item);

  const seekEmailSuccess = useSelector(
    (state: any) => state.seek.email.success,
  );
  const seekEmailResponse = useSelector((state: any) => {
    const body = state.seek.email.body;
    if(!body){
      return body;
    }

    return `${body}
      <p>&nbsp;</p>
      <div class="mceNonEditable widget" data-widget-id="assets.grid">
        {{> AssetAttachments}}
      </div>    
    `
  });
  const seekEmailLoading = useSelector(
    (state: any) => state.seek.email.loading,
  );

  const { trackSeekResponseReactedEvent, trackSeekDraftAnEmailClickedEvent } =
    useSeekAdoption();

  useEffect(() => {
    if (chat.loading && !loadingMessage) {
      const messages = [
        "Rummaging through the archives to unravel the mysteries of the universe...just a few more moments!",
        "Searching for answers in all documents, so you don't have to go full detective mode!",
        "Roses are red, violets are blue, searching for answers, just for you!",
        "Digging through the digital haystack, to find the needle of knowledge you seek!",
        "Putting on my detective hat and scouring the documents for clues to solve the mystery!",
      ];
      const randomIndex = Math.floor(Math.random() * messages.length);
      setLoadingMessage(messages[randomIndex]);
      if (scrollToBottom) {
        scrollToBottom();
      }
    }
  }, [chat.loading, loadingMessage]);

  useEffect(() => {
    if (!chat.loading) {
      setLoadingMessage(null);
    }
  }, [chat.loading]);

  useEffect(() => {
    if (skipTypewriting) {
      setShowAssets(true);
    }
  }, [skipTypewriting]);

  useEffect(() => {
    if (showAssets && scrollToBottom) {
      scrollToBottom();
    }
  }, [showAssets]);

  useEffect(() => {
    if (seekEmailSuccess && !_.isEmpty(seekEmailResponse)) {
      setShowCompose(true);
      if (seekAsset) {
        dispatch(selectAsset(asset));
      } else {
        chat.contents.map((content: any) => {
          if (content.settings.share) {
            dispatch(selectAsset(content));
          }
        });
      }
    } else {
      setShowCompose(false);
      chat?.contents?.map((content: any) => {
        dispatch(deselectAsset(content));
      });
    }
  }, [seekEmailSuccess]);

  useEffect(() => {
    setIsHelpful(chat.helpful);
  }, [chat.helpful]);

  const onComplete = useCallback(() => {
    setShowAssets(true);
    handleComplete(index);
  }, [handleComplete, index]);

  const handleDraftEmail = () => {
    let assets = chat.contents?.filter(asset => asset.settings.share);
    if (assets?.length > 0) {
      setAssetsForCompose([...assets]);
    } else {
      setAssetsForCompose(null);
    }
    const assetTypes = assets.map((asset) =>
      FileService.getFileType(asset.metadata),
    );

    trackSeekDraftAnEmailClickedEvent({
      assetTypes,
      sourceCta: seekAsset ? ASSET_DETAIL : SEEK,
    });
    dispatch(fetchSeekEmailResponse({ chatId: chat.id, id: chat.session.id }));
  };

  const handleDiscardEmail = () => {
    setShowCompose(false);
    dispatch(invalidateSeekEmailResponse());
  };

  const handleFeedback = (helpful) => {
    if (helpful !== isHelpful) {
      setIsHelpful(helpful);
      dispatch(updateSeekFeedback({ chatId: chat.id, isHelpful: helpful }));
      const reactionType = helpful ? "Like" : "Dislike";
      const sourceCta = seekAsset ? ASSET_DETAIL : SEEK;
      trackSeekResponseReactedEvent({ reactionType, sourceCta });
    }
  };

  const transformAnswer = useMemo(() => {
    const citationRegex =
      /<span\s+class=['"]seek__cite['"]\s+data-cite=['"](\d+)['"]>\[\d+\]<\/span>/g;

    const uniqueCitations = _.uniqBy(chat.citations, 'id');
    const sources = uniqueCitations.map((citation: any) => {
      const content = chat.contents.find(
        (content: any) => content.id === citation.content.id,
      );
      return {
        id: content?.id,
        name: content?.name || "",
        icon: content?.icon?.thumbnail || "",
        metadata: content?.metadata || {},
        page: citation.page,
      };
    });

    const transformedParts: Array<string | React.JSX.Element> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = citationRegex.exec(chat.answer)) !== null) {
      const citeId = parseInt(match[1], 10);
      const index = match.index;
      const asset = sources[citeId - 1];

      if (lastIndex < index) {
        transformedParts.push(chat.answer.substring(lastIndex, index));
      }

      transformedParts.push(
        <span
          className={styles.citationNumber}
          onClick={() => {
            if (seekAsset) {
              const pageContainer = document.querySelector(
                `#asset_viewer_${asset.id} [data-page='${asset.page}']`,
              );
              if (!pageContainer) {
                return;
              }

              pageContainer.scrollIntoView(true);
            } else {
              window.open(`/assets/${asset.id}`, "_blank");
            }
          }}
        >
          <div
            key={`cite-${citeId}-${index}`}
            className={styles.seek__cite}
            // target="_blank"
            rel="noopener noreferrer"
            data-cite={citeId}
          >
            <span className={styles.citationId}>{asset.page === -1 ? citeId : asset.page}</span>
            {!seekAsset && (
              <div className={styles.hoverTooltip}>
                <Image className={styles.icon} src={asset?.icon} />
                <div className={styles.assetDetails}>
                  <Text text={asset?.name} className={styles.assetTitle} lines={1} />
                  <p className={styles.assetMetadata}>
                    {FileService.getFileType(asset.metadata)}{" "}
                    {asset.metadata.pages
                      ? `| ${asset.metadata.pages} pages`
                      : asset.metadata.video_duration
                        ? `| ${Math.ceil(asset.metadata.video_duration / 60)} min`
                        : ""}
                  </p>
                </div>
              </div>
            )}
          </div>
        </span>,
      );

      lastIndex = citationRegex.lastIndex;
    }

    if (lastIndex < chat.answer.length) {
      transformedParts.push(chat.answer.substring(lastIndex));
    }

    return transformedParts;
  }, [chat]);
  
  const seekLoadingMessage = React.useMemo(() => (loadingMessage ? [loadingMessage] : []), [loadingMessage]);

  return (
    <div key={index}>
      <div className={styles.questionContainer}>
        <Image src={profile.icon.thumbnail} className={styles.profileIcon} />
        <p className={cx(styles.question, seekAsset && styles.assetQuestion)}>
          {chat.question}
        </p>
      </div>
      {chat.loading && loadingMessage ? (
        <div className={styles.loadingMessage}>
          <TypeWriter
            seekAsset={seekAsset}
            key={`loading-${index}`}
            textParts={seekLoadingMessage}
            scrollToBottom={scrollToBottom}
          />
        </div>
      ) : (
        chat.answer && (
          <div className={styles.answerContainer}>
            <div className={cx(styles.headerContainer, styles.responseHeaderContainer)}>
              <p className={styles.headerTitle}>Response</p>
              <div className={styles.separator}></div>
            </div>
            {skipTypewriting ? (
              <div
                className={cx(styles.answer, seekAsset && styles.assetAnswer)}
              >
                {transformAnswer.map((part, idx) => (
                  <React.Fragment key={idx}>{part}</React.Fragment>
                ))}
              </div>
            ) : (
              <TypeWriter
                seekAsset={seekAsset}
                key={`answer-${index}`}
                textParts={transformAnswer}
                scrollToBottom={scrollToBottom}
                onComplete={onComplete}
              />
            )}
          </div>
        )
      )}
      {showAssets && _.size(chat.contents) > 0 && !seekAsset && (
        <>
          <div className={cx(styles.headerContainer, styles.citationsHeaderContainer)}>
            <p className={styles.citationsTitle}>Sources</p>
            <div className={styles.separator}></div>
          </div>
          <div className={styles.citations}>
            {chat.contents.map((content: any) => (
              <div className={styles.citation} key={content.id}>
                <a
                  href={`${"/assets/" + content.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.asset}
                >
                  <div className={styles.assetIcon}>
                    <AssetIcon asset={content} />
                    <div className={styles.metadata}>
                      {FileService.getFileType(content.metadata)}
                    </div>
                  </div>
                  <div className={styles.assetDetails}>
                    <Text className={styles.assetTitle} text={content.name} lines={1} />
                  </div>
                </a>
              </div>
            ))}
          </div>
        </>
      )}
      <div className={styles.assetActions}>
        {showAssets && _.size(chat.contents) > 0 && !loadingMessage && (
          <>
            <span
              className={cx(
                styles.action, 
                seekEmailLoading ? styles.selectedAction : styles.expandOnHover,
              )}
              onClick={() => !seekEmailLoading && handleDraftEmail()}
            >
              <i
                className={cx(
                  "paperflite-send",
                  seekEmailLoading && styles.loading,
                )}
              />
              <span
                className={cx(
                  seekEmailLoading && styles.loading,
                  styles.actionText,
                )}
              >
                {seekEmailLoading ? "drafting an email" : "draft an email"}
              </span>
            </span>
            {!seekAsset && (
              <span
                className={cx(
                  styles.action, 
                  seekEmailLoading ? '' : styles.expandOnHover,
                )}
                onClick={() => !seekEmailLoading && setShowAddToCollection(true)}
              >
                <i 
                  className={cx(
                    "paperflite-add", 
                    seekEmailLoading && styles.loading,
                  )} 
                />
                <span 
                  className={cx(
                    styles.actionText,
                    seekEmailLoading && styles.loading,
                  )}
                >add to collection</span>
              </span>
            )}
            <div className={styles.divider}></div>
          </>
        )}
        {showAssets && !loadingMessage && (
          <>
            <i
              onClick={() => !seekEmailLoading && handleFeedback(true)}
              className={cx(
                "paperflite-thumbs-up-unslected",
                seekEmailLoading && styles.loading,
                isHelpful != null && isHelpful && styles.selected,
              )}
            />
            <i
              onClick={() => !seekEmailLoading && handleFeedback(false)}
              className={cx(
                "paperflite-thumbs-down-unselected",
                seekEmailLoading && styles.loading,
                isHelpful != null && !isHelpful && styles.selected,
              )}
            />
          </>
        )}
      </div>


      <Slider.Dialog
        width={"380px"}
        show={showAddToCollection}
        onClose={() => setShowAddToCollection(false)}
      >
        {showAddToCollection && (
          <AddToCollection
            onClose={() => setShowAddToCollection(false)}
            show={showAddToCollection}
            assets={chat.contents}
            sourceType = "Seek"
          />
        )}
      </Slider.Dialog>
      {showDivider && showAssets && !loadingMessage && <div className={styles.divider}></div>}
    </div>
  );
};

export default ChatAnswer;
